/**
 * 装配体基准引用依赖解析器。
 *
 * 文件职责：
 * 1. 从基准点、轴、面的可复算定义中收集创建时保存的 AssemblyTopologySelectionRef；
 * 2. 将参考拓扑归并为 Mate 求解器可消费的驱动组件集合；
 * 3. 只处理可序列化业务数据，不读取或返回 Three.js 运行时对象。
 */

import {
  AssemblyReferenceDependencyKind,
  ASSEMBLY_REFERENCE_DEPENDENCY_SCHEMA_VERSION,
  type AssemblyReferenceDependencySnapshot,
} from '../definition';

/**
 * 可提供装配拓扑引用的基准定义片段。
 */
interface AssemblyReferenceTopologyCarrier {
  /** 创建基准时保存的装配拓扑稳定引用。 */
  readonly assemblyTopologyRef?: AssemblyTopologySelectionRef | null;
}

/**
 * 可提供基准面计算来源的对象片段。
 */
interface DatumPlaneDependencySource {
  /** 读取基准面的可复算定义来源。 */
  getCalculationSource?(): {
    /** 创建该基准面的参考集合。 */
    readonly references?: readonly AssemblyReferenceTopologyCarrier[];
  } | null;
}

/**
 * 可提供基准轴定义的记录片段。
 */
interface AxisFeatureDependencyRecord {
  /** 当前记录生成的正式基准轴 ID。 */
  readonly datumAxisId?: number;
  /** 基准轴可复算定义。 */
  readonly definition?: {
    /** 创建该基准轴的参考集合。 */
    readonly references?: readonly AssemblyReferenceTopologyCarrier[];
  };
}

/**
 * 可提供基准点定义的记录片段。
 */
interface PointFeatureDependencyRecord {
  /** 当前记录生成的正式基准点 ID 集合。 */
  readonly datumPointIds?: readonly number[];
  /** 基准点可复算定义。 */
  readonly definition?: {
    /** 创建该基准点的参考集合。 */
    readonly references?: readonly AssemblyReferenceTopologyCarrier[];
  };
}

/**
 * 基准依赖解析宿主。
 *
 * 设计说明：
 * Viewer、测试桩或后续离线文档恢复器只需要实现这个窄接口即可复用解析逻辑，
 * Mate 求解器不直接依赖具体 Reference*Controller。
 */
export interface AssemblyReferenceDependencyResolverHost {
  /** 按 ID 读取基准面。 */
  readonly getDatumPlane?: (planeId: number) => DatumPlaneDependencySource | undefined | null;
  /** 读取当前基准轴特征记录。 */
  readonly getDatumAxisFeatureRecords?: () => readonly AxisFeatureDependencyRecord[];
  /** 读取当前基准点特征记录。 */
  readonly getDatumPointFeatureRecords?: () => readonly PointFeatureDependencyRecord[];
}

/**
 * 克隆装配拓扑引用。
 *
 * @param ref 原始拓扑引用。
 * @returns 独立拓扑引用副本。
 */
function cloneAssemblyTopologySelectionRef(
  ref: AssemblyTopologySelectionRef,
): AssemblyTopologySelectionRef {
  return {
    ...ref,
    adjacentFaceRefs: ref.adjacentFaceRefs ? [...ref.adjacentFaceRefs] : undefined,
  };
}

/**
 * 生成拓扑引用去重键。
 *
 * @param ref 拓扑引用。
 * @returns 去重键。
 */
function createTopologyRefKey(ref: AssemblyTopologySelectionRef): string {
  return [
    ref.assemblyNodeId,
    ref.partUuid ?? '',
    ref.bodyBindingId,
    ref.entityKind,
    ref.persistentRef,
  ].join('|');
}

/**
 * 归一化拓扑引用集合。
 *
 * @param topologyRefs 原始拓扑引用。
 * @returns 去重后的拓扑引用副本。
 */
function normalizeTopologyRefs(
  topologyRefs: readonly (AssemblyTopologySelectionRef | null | undefined)[],
): AssemblyTopologySelectionRef[] {
  const topologyRefByKey = new Map<string, AssemblyTopologySelectionRef>();
  topologyRefs.forEach((ref) => {
    if (!ref || typeof ref.assemblyNodeId !== 'string' || ref.assemblyNodeId.length === 0) {
      return;
    }
    topologyRefByKey.set(createTopologyRefKey(ref), cloneAssemblyTopologySelectionRef(ref));
  });
  return Array.from(topologyRefByKey.values());
}

/**
 * 从引用片段中提取装配拓扑引用。
 *
 * @param references 基准定义引用集合。
 * @returns 拓扑引用集合。
 */
function collectTopologyRefsFromCarriers(
  references: readonly AssemblyReferenceTopologyCarrier[] | undefined,
): AssemblyTopologySelectionRef[] {
  return normalizeTopologyRefs((references ?? []).map((reference) => reference.assemblyTopologyRef));
}

/**
 * 从基准面稳定引用中收集驱动拓扑。
 *
 * @param referenceRef 基准稳定引用。
 * @param host 解析宿主。
 * @returns 驱动拓扑集合。
 */
function collectDatumPlaneTopologyRefs(
  referenceRef: AssemblyReferenceSelectionRef,
  host: AssemblyReferenceDependencyResolverHost,
): AssemblyTopologySelectionRef[] {
  const datumPlane = host.getDatumPlane?.(referenceRef.referenceId);
  const calculationSource = datumPlane?.getCalculationSource?.() ?? null;
  return collectTopologyRefsFromCarriers(calculationSource?.references);
}

/**
 * 从基准轴稳定引用中收集驱动拓扑。
 *
 * @param referenceRef 基准稳定引用。
 * @param host 解析宿主。
 * @returns 驱动拓扑集合。
 */
function collectDatumAxisTopologyRefs(
  referenceRef: AssemblyReferenceSelectionRef,
  host: AssemblyReferenceDependencyResolverHost,
): AssemblyTopologySelectionRef[] {
  const record = host.getDatumAxisFeatureRecords?.()
    .find((item) => item.datumAxisId === referenceRef.referenceId);
  return collectTopologyRefsFromCarriers(record?.definition?.references);
}

/**
 * 从基准点稳定引用中收集驱动拓扑。
 *
 * @param referenceRef 基准稳定引用。
 * @param host 解析宿主。
 * @returns 驱动拓扑集合。
 */
function collectDatumPointTopologyRefs(
  referenceRef: AssemblyReferenceSelectionRef,
  host: AssemblyReferenceDependencyResolverHost,
): AssemblyTopologySelectionRef[] {
  const record = host.getDatumPointFeatureRecords?.()
    .find((item) => (item.datumPointIds ?? []).includes(referenceRef.referenceId));
  return collectTopologyRefsFromCarriers(record?.definition?.references);
}

/**
 * 根据基准类型收集驱动拓扑。
 *
 * @param referenceRef 基准稳定引用。
 * @param host 解析宿主。
 * @returns 驱动拓扑集合。
 */
function collectReferenceTopologyRefs(
  referenceRef: AssemblyReferenceSelectionRef,
  host: AssemblyReferenceDependencyResolverHost,
): AssemblyTopologySelectionRef[] {
  if (referenceRef.kind === 'datum-plane') {
    return collectDatumPlaneTopologyRefs(referenceRef, host);
  }
  if (referenceRef.kind === 'datum-axis') {
    return collectDatumAxisTopologyRefs(referenceRef, host);
  }
  if (referenceRef.kind === 'datum-point') {
    return collectDatumPointTopologyRefs(referenceRef, host);
  }
  return [];
}

/**
 * 根据拓扑引用解析基准依赖快照。
 *
 * @param input 输入数据。
 * @returns 基准依赖快照。
 */
export function resolveAssemblyReferenceDependencySnapshotFromTopologyRefs(input: {
  /** 基准自身稳定引用。 */
  readonly referenceStableRef: string;
  /** 创建基准时记录的拓扑引用。 */
  readonly topologyRefs: readonly (AssemblyTopologySelectionRef | null | undefined)[];
}): AssemblyReferenceDependencySnapshot {
  const driverTopologyRefs = normalizeTopologyRefs(input.topologyRefs);
  const driverComponentInstanceIds = Array.from(new Set(
    driverTopologyRefs.map((ref) => ref.assemblyNodeId),
  ));
  if (driverComponentInstanceIds.length === 0) {
    return {
      schemaVersion: ASSEMBLY_REFERENCE_DEPENDENCY_SCHEMA_VERSION,
      kind: AssemblyReferenceDependencyKind.Fixed,
      referenceStableRef: input.referenceStableRef,
      driverComponentInstanceIds: [],
      driverTopologyRefs: [],
    };
  }
  if (driverComponentInstanceIds.length === 1) {
    return {
      schemaVersion: ASSEMBLY_REFERENCE_DEPENDENCY_SCHEMA_VERSION,
      kind: AssemblyReferenceDependencyKind.SingleComponent,
      referenceStableRef: input.referenceStableRef,
      driverComponentInstanceIds,
      driverTopologyRefs,
    };
  }
  return {
    schemaVersion: ASSEMBLY_REFERENCE_DEPENDENCY_SCHEMA_VERSION,
    kind: AssemblyReferenceDependencyKind.MultiComponent,
    referenceStableRef: input.referenceStableRef,
    driverComponentInstanceIds,
    driverTopologyRefs,
    message: '该派生基准由多个组件共同驱动，当前按装配体参考几何参与配合。',
  };
}

/**
 * 解析基准引用依赖快照。
 *
 * @param referenceRef 基准稳定引用。
 * @param host 解析宿主。
 * @returns 基准依赖快照；默认固定基准返回 `null`，避免污染旧数据。
 */
export function resolveAssemblyReferenceDependencySnapshot(
  referenceRef: AssemblyReferenceSelectionRef,
  host: AssemblyReferenceDependencyResolverHost,
): AssemblyReferenceDependencySnapshot | null {
  const snapshot = resolveAssemblyReferenceDependencySnapshotFromTopologyRefs({
    referenceStableRef: referenceRef.stableRef,
    topologyRefs: collectReferenceTopologyRefs(referenceRef, host),
  });
  return snapshot.kind === AssemblyReferenceDependencyKind.Fixed ? null : snapshot;
}
